import { SubdivisionAuditInterface } from '../interfaces/subdivision-audit.interface';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import {
  IsBoolean,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';
import { Exclude, Expose, Type } from 'class-transformer';
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { AuditActionTypeEnum } from '../../common/enums/audit-action-type.enum';
import { SubdivisionWarrantyStatusEnum } from '../../subdivision/enums/subdivision-warranty-status.enum';
import { SubdivisionOverrideTypeEnum } from '../../subdivision/enums/subdivision-override-type.enum';
import { PhaseStructureTypeEnum } from '../../phase/enums/phase-structure-type.enum';
import { UserInterface } from '../../user/interfaces/user.interface';
import { UserDto } from '../../user/dto/user.dto';
import {
  REGION_COST_PER_BOARD_MIN,
  REGION_COST_PER_SQFT_MIN,
} from '../../region/constants/region-validation.constants';

@Exclude()
export class SubdivisionAuditDto
  extends CommonEntityDto
  implements SubdivisionAuditInterface
{
  @ApiProperty({
    format: 'uuid',
    description: 'Id of the subdivision this history record belongs to.',
  })
  @IsUUID()
  @Expose()
  subdivisionId!: string;

  @ApiProperty({
    type: 'enum',
    enum: AuditActionTypeEnum,
    title: 'Action Type',
    description: 'The type of action that created this audit record.',
  })
  @IsEnum(AuditActionTypeEnum)
  @Expose()
  actionType!: AuditActionTypeEnum;

  @ApiProperty({
    format: 'uuid',
    description: 'Id of the user who made this change.',
  })
  @IsUUID()
  @Expose()
  changedByUserId!: UserInterface['id'];

  @ApiPropertyOptional({
    type: () => UserDto,
    description: 'The user who made this change.',
  })
  @Type(() => UserDto)
  @Expose()
  changedByUser?: UserInterface;

  @ApiPropertyOptional({
    description: 'Optional reason for the change.',
  })
  @IsString()
  @IsOptional()
  @MaxLength(500)
  @Expose()
  changeReason?: string;

  @ApiProperty({
    description: 'The name of the Subdivision',
  })
  @IsString()
  @MinLength(2)
  @MaxLength(100)
  @Expose()
  name!: string;

  @ApiProperty({
    description: 'Address line one',
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  address1!: string;

  @ApiPropertyOptional({
    description: 'Address line two',
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @IsOptional()
  @Expose()
  address2?: string;

  @ApiProperty({
    description: 'City',
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  city!: string;

  @ApiProperty({
    description: 'State',
  })
  @IsString()
  @MinLength(1)
  @MaxLength(100)
  @Expose()
  state!: string;

  @ApiProperty({
    description: 'ZIP code must be in the format 12345 or 12345-6789',
    example: '12345-6789',
  })
  @IsNotEmpty()
  @Matches(/^\d{5}(-\d{4})?$/, {
    message:
      'Zip code must be entered either in 5 digit format (12345) or 9 digit format (12345-1234)',
  })
  @Expose()
  zip!: string;

  @ApiProperty({
    type: 'boolean',
    default: true,
    title: 'Active?',
    description: 'True if subdivision is active',
  })
  @IsBoolean()
  @Expose()
  active!: boolean;

  @ApiProperty({
    type: 'boolean',
    default: true,
    title: 'reqBumpoutTimesheet?',
    description: 'Bumpout Flow',
  })
  @IsBoolean()
  @Expose()
  reqBumpoutTimesheet!: boolean;

  @ApiPropertyOptional({
    type: 'boolean',
    title: 'Archived?',
    description: 'True if subdivision is archived',
  })
  @IsBoolean()
  @IsOptional()
  @Expose()
  archived?: boolean;

  @ApiPropertyOptional({
    type: 'boolean',
    title: 'overrideRegionCostPerBoard?',
    description: 'Override Region Cost Per Board',
  })
  @IsBoolean()
  @IsOptional()
  @Expose()
  overrideRegionCostPerBoard?: boolean;

  @ApiPropertyOptional({
    type: 'enum',
    enum: SubdivisionOverrideTypeEnum,
    title: 'Override Type',
    description: 'The override type for subdivision costs',
  })
  @IsEnum(SubdivisionOverrideTypeEnum)
  @IsOptional()
  @Expose()
  overrideType?: SubdivisionOverrideTypeEnum;

  @ApiPropertyOptional({
    type: 'boolean',
    title: 'Allow Payout Amount Editing',
    description:
      'Allow Supervisor to edit the contractor payout amount for Piecework/Special Jobs repairs',
  })
  @IsBoolean()
  @IsOptional()
  @Expose()
  allowPayoutAmountEditing?: boolean;

  @ApiProperty({
    title: 'Garage Cost Fixed',
    description: 'Garage Cost Fixed',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  garageCostFixed!: number | null;

  @ApiProperty({
    title: 'Hanging Cost Per Board',
    description: 'Hanging Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwHangingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Finishing Cost Per Board',
    description: 'Finishing Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwFinishingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Spraying Cost Per Board',
    description: 'Spraying Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwSprayingCostPerBoard!: number | null;

  @ApiProperty({
    format: 'uuid',
    description: 'Id of the builder which owns this subdivision.',
  })
  @IsUUID()
  @Expose()
  builderId!: string;

  @ApiProperty({
    type: 'enum',
    enum: SubdivisionWarrantyStatusEnum,
    title: 'Subdivision Warranty Status',
    description: 'The current status of the subdivision warranty.',
  })
  @IsEnum(SubdivisionWarrantyStatusEnum)
  @Expose()
  warrantyStatus!: SubdivisionWarrantyStatusEnum;

  @ApiProperty({
    format: 'uuid',
    description: 'Id of the region which this subdivision belongs to.',
  })
  @IsUUID()
  @Expose()
  regionId!: string;

  @ApiProperty({
    title: 'HighCeiling Per Board',
    description: 'HighCeiling Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  highCeilingPerBoard!: number | null;

  @ApiProperty({
    type: 'enum',
    enum: PhaseStructureTypeEnum,
    nullable: true,
    title: 'Resource Type',
    description: 'The resource type of this Phase',
  })
  @IsEnum(PhaseStructureTypeEnum)
  @Expose()
  phaseStructureType!: PhaseStructureTypeEnum;

  @ApiProperty({
    title: 'Hanging Cost Per Board',
    description: 'Drywall and Paint Hanging Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpHangingCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Finish Cost Per Board',
    description: 'Drywall and Paint Finish Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpFinishCostPerBoard!: number | null;

  @ApiProperty({
    title: 'Prime Cost Per Sqft',
    description: 'Drywall and Paint Prime Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpPrimeCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Bump 1 Cost Per Board',
    description: 'Drywall and Paint Bump 1 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump1CostPerBoard!: number | null;

  @ApiProperty({
    title: 'Trim 1 Cost Per Sqft',
    description: 'Trim 1 Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpTrim1CostPerSqft!: number | null;

  @ApiProperty({
    title: 'Interior Paint Cost Per Sqft',
    description: 'Drywall and Paint Interior Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpInteriorPaintCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Bump 2 Cost Per Board',
    description: 'Drywall and Paint Bump 2 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump2CostPerBoard!: number | null;

  @ApiProperty({
    title: 'Trim 2 Cost Per Sqft',
    description: 'Trim 2 Hardware Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpTrim2CostPerSqft!: number | null;

  @ApiProperty({
    title: 'Final Paint Cost Per Sqft',
    description: 'Drywall and Paint Final Paint Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpFinalPaintCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Bump 3 Cost Per Board',
    description: 'Drywall and Paint Bump 3 Cost Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  dwpBump3CostPerBoard!: number | null;

  @ApiProperty({
    title: 'Paint CMBluetape Cost Per Sqft',
    description: 'Drywall and Paint CMBluetape Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpCMBluetapeCostPerSqft!: number | null;

  @ApiProperty({
    title: 'FinalBluetape Cost Per Sqft',
    description: 'Drywall and Paint FinalBluetape Cost Per Sqft',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_SQFT_MIN)
  @Expose()
  dwpFinalBluetapeCostPerSqft!: number | null;

  @ApiProperty({
    title: 'Screw Inspection Per Board',
    description: 'Screw Inspection Per Board',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(REGION_COST_PER_BOARD_MIN)
  @Expose()
  screwInspectionPerBoard!: number | null;

  @ApiProperty({
    title: 'Special Jobs Percentage',
    description: 'Special Jobs Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(0)
  @Max(100)
  @Expose()
  specialJobsPercentage!: number | null;

  @ApiProperty({
    title: 'Piecework Percentage',
    description: 'Piecework Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(0)
  @Max(100)
  @Expose()
  pieceworkPercentage!: number | null;

  @ApiProperty({
    title: 'Interior Options Percentage',
    description: 'Interior Options Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  interiorOptionsPercentage!: number;

  @ApiProperty({
    title: 'Exterior Percentage',
    description: 'Exterior Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  exteriorOptionsPercentage!: number;

  @ApiProperty({
    title: 'Trim Option Percentage',
    description: 'Trim Option Percentage',
  })
  @IsNumber({ maxDecimalPlaces: 2 })
  @Expose()
  trimOptionPercentage!: number;
}
